import React from 'react';
import {IProductPurchaseStats} from '../../models/Search';
import {openEntity} from '../GlobalSearch/useGlobalSearchHotkey';
import PurchaseStatsPeek from './PurchaseStatsPeek';

interface IPurchaseStatsCellProps {
    stats?: IProductPurchaseStats;
    /** When set, clicking the cell opens this product's details on its Purchase History tab. */
    productId?: string;
    /** With the product id, lets the hover peek load the recent purchases behind the count. */
    catalog?: string;
}

const formatUnits = (n: number): string =>
    Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, {maximumFractionDigits: 2});

/**
 * The Ordered column: how much of a product has sold, in the same three-slot figure layout as the other
 * number cells. Units ordered is the figure, the order count sits beneath it. A product with no sales
 * reads "Never" in neutral grey. The counts are denormalized onto the product by the order workers, so
 * they can lag a sale by a little.
 */
const PurchaseStatsCell: React.FC<IPurchaseStatsCellProps> = ({stats, productId, catalog}) => {
    const open = productId
        ? (e: React.MouseEvent) => { e.stopPropagation(); openEntity({kind: 'product', productId, initialTab: 'purchaseHistory'}); }
        : undefined;
    const clickProps = open ? {onClick: open, style: {cursor: 'pointer'}, title: 'Open the purchase history'} : {};

    if (!stats || !stats.orderCount) {
        return (
            <div className='figure-cell cell-neutral' {...clickProps}>
                <span className='figure-top' />
                <span className='figure-main' style={{fontWeight: 400, opacity: 0.6}}>Never</span>
            </div>
        );
    }

    const last = stats.lastOrdered ? new Date(stats.lastOrdered) : undefined;
    const title = last && !isNaN(last.getTime())
        ? `Last ordered ${last.toLocaleDateString()}${open ? '. Open the purchase history' : ''}`
        : clickProps.title;

    // The native title would fight the peek flyout, so it only stays when there is no peek.
    return (
        <PurchaseStatsPeek productId={productId} catalog={catalog} stats={stats}>
            {/* Plain text throughout: green is reserved for stock and priced figures; a sales count is neither. */}
            <div className='figure-cell cell-neutral' {...clickProps} title={catalog ? undefined : title}>
                <span className='figure-top'>{stats.windowMonths ? `Last ${stats.windowMonths} mo` : 'Ordered'}</span>
                <span className='figure-main'>{formatUnits(stats.quantityOrdered)}</span>
                <span className='figure-bottom' style={{fontStyle: 'normal'}}>
                    {stats.orderCount === 1 ? '1 order' : `${stats.orderCount.toLocaleString()} orders`}
                </span>
            </div>
        </PurchaseStatsPeek>
    );
};

export default PurchaseStatsCell;
